import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Star, Heart, MessageCircle, Send, Trash2, BadgeCheck, User, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CommentThread, timeAgo } from "../pages/Feed";

const loginState = { from: "/e-book" };

const Stars = ({ value, onChange, size = "h-5 w-5", testid }) => (
  <div className="flex gap-0.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        disabled={!onChange}
        onClick={() => onChange?.(n)}
        data-testid={onChange ? `star-${n}` : undefined}
        aria-label={`${n} star${n > 1 ? "s" : ""}`}
        className={onChange ? "transition-transform hover:scale-110" : "cursor-default"}
      >
        <Star className={`${size} ${n <= value ? "fill-[#e0a82e] text-[#e0a82e]" : "text-[#555]"}`} />
      </button>
    ))}
  </div>
);

const Composer = ({ onPosted }) => {
  const { user } = useAuth();
  const [rating, setRating] = useState(5);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!user)
    return (
      <p className="rounded-md border border-white/10 bg-[#242424] p-5 text-sm text-[#b0b0b0]" data-testid="testimonial-login-prompt">
        <Link to="/login" state={loginState} className="text-[#e0a82e] hover:underline">Log in</Link> or{" "}
        <Link to="/register" state={loginState} className="text-[#e0a82e] hover:underline">register</Link> to share your thoughts on the e-book.
      </p>
    );

  const submit = async () => {
    if (!body.trim()) return setErr("Please write your thoughts first.");
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/testimonials", { body, rating });
      onPosted(data); setBody(""); setRating(5);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not post your testimonial.");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-white/10 bg-[#242424] p-5" data-testid="testimonial-composer">
      <div className="mb-3 flex items-center gap-3">
        <span className="text-sm text-[#b0b0b0]">Your rating</span>
        <Stars value={rating} onChange={setRating} testid="testimonial-rating-input" />
      </div>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={4}
        maxLength={3000}
        placeholder="What did you think of 10 Years in 10 Prisons?"
        data-testid="testimonial-input"
        className="w-full resize-y rounded border border-white/15 bg-[#1e1e1e] px-3 py-2 text-sm text-[#eee] outline-none focus:border-[#e0a82e]"
      />
      {err && <p className="mt-2 text-sm text-[#e0574c]" data-testid="testimonial-error">{err}</p>}
      <button
        onClick={submit}
        disabled={busy}
        data-testid="testimonial-submit"
        className="mt-3 inline-flex items-center gap-2 rounded bg-[#e0a82e] px-5 py-2 font-display text-sm uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f] disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post Testimonial
      </button>
    </div>
  );
};

const CommentsSection = ({ t, comments, setComments, onCount }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [text, setText] = useState("");

  const like = async (c) => {
    if (!user) return navigate("/login", { state: loginState });
    const { data } = await api.post(`/testimonials/comments/${c.id}/like`);
    setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data } : x)));
  };

  const add = async (body, parentId = null) => {
    if (!body.trim()) return false;
    if (!user) { navigate("/login", { state: loginState }); return false; }
    const { data } = await api.post(`/testimonials/${t.id}/comments`, { body, parent_id: parentId });
    setComments((prev) => [...prev, data]);
    onCount(1);
    if (!parentId) setText("");
    return true;
  };

  const remove = async (c) => {
    if (!window.confirm("Remove this comment (and its replies)?")) return;
    await api.delete(`/testimonials/comments/${c.id}`);
    const gone = comments.filter((x) => x.id === c.id || x.parent_id === c.id).length;
    setComments((prev) => prev.filter((x) => x.id !== c.id && x.parent_id !== c.id));
    onCount(-gone);
  };

  return (
    <div className="mt-4 space-y-3">
      {comments.filter((c) => !c.parent_id).map((c) => (
        <CommentThread
          key={c.id}
          comment={c}
          replies={comments.filter((r) => r.parent_id === c.id)}
          onLike={like}
          onReply={(target, body) => add(body, target.id)}
          onDelete={user?.is_admin ? remove : undefined}
        />
      ))}
      {user ? (
        <div className="flex items-center gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add(text)}
            placeholder="Write a comment..."
            data-testid={`testimonial-comment-input-${t.id}`}
            className="flex-1 rounded border border-white/15 bg-[#1e1e1e] px-3 py-2 text-sm text-[#eee] outline-none focus:border-[#e0a82e]"
          />
          <button onClick={() => add(text)} data-testid={`testimonial-comment-send-${t.id}`} className="rounded bg-[#e0a82e] p-2 text-[#1c1c1c] hover:bg-[#f2b93f]">
            <Send className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <p className="text-xs text-[#9a9a9a]">
          <Link to="/login" state={loginState} className="text-[#e0a82e]">Log in</Link> to join the conversation.
        </p>
      )}
    </div>
  );
};

const TestimonialCard = ({ t, focused, onChanged, onDeleted }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [comments, setComments] = useState(null);

  const toggle = async () => {
    if (!open && comments === null) {
      const { data } = await api.get(`/testimonials/${t.id}/comments`);
      setComments(data);
    }
    setOpen((o) => !o);
  };

  useEffect(() => {
    if (!focused) return;
    (async () => {
      if (comments === null) {
        const { data } = await api.get(`/testimonials/${t.id}/comments`);
        setComments(data);
      }
      setOpen(true);
      setTimeout(() => document.getElementById(`testimonial-${t.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const like = async () => {
    if (!user) return navigate("/login", { state: loginState });
    const { data } = await api.post(`/testimonials/${t.id}/like`);
    onChanged({ ...t, ...data });
  };

  const remove = async () => {
    if (!window.confirm("Remove this testimonial?")) return;
    await api.delete(`/testimonials/${t.id}`);
    onDeleted(t.id);
  };

  return (
    <div
      id={`testimonial-${t.id}`}
      data-testid={`testimonial-${t.id}`}
      className={`scroll-mt-24 rounded-md border bg-[#242424] p-5 transition-colors duration-700 ${focused ? "border-[#e0a82e]" : "border-white/10"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#333] text-[#e0a82e]"><User className="h-5 w-5" /></div>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Link to={`/u/${encodeURIComponent(t.author)}`} className="font-display text-base text-[#f0f0f0] hover:underline">{t.author}</Link>
              {t.verified && (
                <span data-testid={`verified-badge-${t.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#e0a82e]/15 px-2 py-0.5 text-[11px] font-600 uppercase tracking-wide text-[#e0a82e]">
                  <BadgeCheck className="h-3.5 w-3.5" /> Verified buyer
                </span>
              )}
            </div>
            <p className="text-xs text-[#8f8f8f]">{timeAgo(t.createdAt)}</p>
          </div>
        </div>
        {user?.is_admin && (
          <button onClick={remove} data-testid={`testimonial-delete-${t.id}`} title="Remove" className="rounded p-2 text-[#8f8f8f] transition-colors hover:text-[#e0574c]">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="mt-3"><Stars value={t.rating} size="h-4 w-4" testid={`testimonial-rating-${t.id}`} /></div>
      <p className="mt-2 whitespace-pre-wrap leading-relaxed text-[#d8d8d8]">{t.body}</p>
      <div className="mt-4 flex items-center gap-4 border-t border-white/10 pt-3">
        <button
          onClick={like}
          data-testid={`testimonial-like-${t.id}`}
          className={`flex items-center gap-1.5 text-sm transition-colors ${t.likedByMe ? "text-[#e0574c]" : "text-[#c0c0c0] hover:text-[#e0574c]"}`}
        >
          <Heart className={`h-5 w-5 ${t.likedByMe ? "fill-[#e0574c]" : ""}`} />
          <span data-testid={`testimonial-like-count-${t.id}`}>{t.likes}</span>
        </button>
        <button onClick={toggle} data-testid={`testimonial-comments-toggle-${t.id}`} className="flex items-center gap-1.5 text-sm text-[#c0c0c0] transition-colors hover:text-[#e0a82e]">
          <MessageCircle className="h-5 w-5" /> {t.comments}
        </button>
      </div>
      {open && comments && (
        <CommentsSection t={t} comments={comments} setComments={setComments} onCount={(d) => onChanged({ ...t, comments: t.comments + d })} />
      )}
    </div>
  );
};

const Testimonials = () => {
  const [items, setItems] = useState(null);
  const location = useLocation();

  useEffect(() => {
    api.get("/testimonials").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const focusId = items && location.hash.startsWith("#testimonial-") ? location.hash.slice(13) : null;
  const onChanged = (t) => setItems((prev) => prev.map((x) => (x.id === t.id ? t : x)));

  return (
    <section className="space-y-4 pt-6" data-testid="testimonials-section">
      <h2 className="border-t border-white/10 pt-8 font-display text-3xl uppercase tracking-wide text-[#e0a82e] md:text-4xl">Testimonials</h2>
      <Composer onPosted={(t) => setItems((prev) => [t, ...(prev || [])])} />
      {items === null ? (
        <p className="py-6 text-center text-sm text-[#9a9a9a]">Loading testimonials...</p>
      ) : items.length === 0 ? (
        <p className="py-6 text-center text-sm text-[#9a9a9a]" data-testid="testimonials-empty">No testimonials yet. Be the first to share your thoughts.</p>
      ) : (
        items.map((t) => (
          <TestimonialCard key={t.id} t={t} focused={focusId === t.id} onChanged={onChanged} onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))} />
        ))
      )}
    </section>
  );
};

export default Testimonials;
